import { put, takeEvery } from 'redux-saga/effects';
import { apiRequest } from './index';
import api from 'utils/api';
import * as authActions from 'actions/Auth';
import { get } from 'lodash';
import jwtDecode from 'jwt-decode';
import history from 'utils/history';
import { logoutUrl } from 'config/environment';
import { checkTokenExpiration, checkRefreshTokenExpiration } from 'utils/token';
import { syncLanguageFromUser, t } from 'i18n';

const loginUser = apiRequest.bind(null, authActions.login, api.login);
const refreshToken = apiRequest.bind(null, authActions.getNewToken, api.refreshToken);
const requestPasswordReset = apiRequest.bind(null, authActions.requestPasswordReset, api.requestPasswordReset);
const resetPassword = apiRequest.bind(null, authActions.resetPassword, api.resetPassword);

const getRoleManagementRepresentation = apiRequest.bind(
  null,
  authActions.getRoleManagementRepresentation,
  api.getRoleManagementRepresantation
);

//TODO: implement multiple role
const getUserRole = (decoded) => {
  const role = get(decoded, 'roles', []).filter((el) => el !== 'uma_authorization' && el !== 'offline_access');
  let roleName = get(role, '0');
  if (roleName === 'ems_admin') roleName = 'admin';
  else if (roleName === 'distributor') roleName = 'group_manager';
  else if (roleName === 'chain_admin') roleName = 'chain_manager';
  else if (roleName === 'studio_admin') roleName = 'group_admin';
  else if (roleName === 'studio_user') roleName = 'user';

  return roleName;
  // return get(decoded, "resource_access.ems-client.roles", []).reduce((acc, el) => {
  //   if (el === "admin" || acc === "admin") return "admin";
  //   if (el === "group_manager" || acc === "group_manager") return "group_manager";
  //   if (el === "chain_manager" || acc || "chain_manager") return "chain_manager";
  //   if (el === "group_admin" || acc || "group_admin") return "group_admin";
  //   if (el === "stats_admin" || acc || "stats_admin") return "stats_admin";
  //   if (el === "home_user" || acc || "home_user") return "home_user";
  //   if (el === "user" || acc || "user") return "user";
  //   return acc;
  // }, "");
};

function* getToken() {
  const token = checkTokenExpiration();
  if (token && token !== 'expired') {
    const decoded = jwtDecode(token);
    const userRole = getUserRole(decoded);

    yield put({ type: 'SET_USER_ROLE', payload: userRole });
    yield put({ type: authActions.GET_TOKEN.SUCCESS, payload: decoded });
  } else {
    if (token === 'expired') {
      const refreshToken = checkRefreshTokenExpiration();
      if (!refreshToken || refreshToken === 'expired') {
        yield put({
          type: authActions.GET_TOKEN.FAILURE,
          payload: { error: { message: t('auth.sessionExpired') } },
        });
      } else {
        yield put({
          type: authActions.GET_NEW_TOKEN.REQUEST,
          data: refreshToken,
        });
      }
    } else yield put({ type: authActions.GET_TOKEN.FAILURE });
  }
}

function* saveToken(action) {
  const { access_token, refresh_token, decoded, isRefresh, id_token } = action.payload;
  const userRole = getUserRole(decoded);
  if (userRole === 'user' || userRole === 'home_user') {
    yield put({
      type: authActions.LOGIN.FAILURE,
      payload: {
        message: t('auth.accessDenied', {
          role: userRole === 'user' ? t('roles.studioUser') : t('roles.homeUser'),
        }),
      },
    });
    window.location.href = `${logoutUrl}&id_token_hint=${id_token}`;
    return;
  }
  window.localStorage.setItem('IQ_TOKEN', access_token);
  window.localStorage.setItem('IQ_ID_TOKEN', id_token);
  window.localStorage.setItem('IQ_REFRESH_TOKEN', refresh_token);
  yield put({
    type: 'SET_USER_ROLE',
    payload: userRole,
    isRedirect: isRefresh ? false : true,
  });
  yield put({ type: authActions.GET_TOKEN.SUCCESS, payload: decoded });
}

function* removeToken() {
  const id_token = window.localStorage.getItem('IQ_ID_TOKEN');
  window.localStorage.removeItem('IQ_TOKEN');
  window.localStorage.removeItem('IQ_ID_TOKEN');
  window.localStorage.removeItem('IQ_REFRESH_TOKEN');
  if (!history.location.pathname.includes('auth-redirect')) {
    yield (window.location.href = `${logoutUrl}&id_token_hint=${id_token}`);
    //redirect to login, otherwise it tries to load organization page with all content
    // yield history.push("/login");
  }
}

function* removeTokenNoRedirect() {
  window.localStorage.removeItem('IQ_TOKEN');
  window.localStorage.removeItem('IQ_ID_TOKEN');
  window.localStorage.removeItem('IQ_REFRESH_TOKEN');
  yield put({ type: 'SET_USER_ROLE', payload: '' });
  yield put({ type: authActions.LOGOUT.SUCCESS });
}

export function* handleInvalidToken() {
  yield put({ type: authActions.LOGOUT.REQUEST });
  yield put({
    type: authActions.GET_TOKEN.FAILURE,
    payload: { error: { message: t('auth.sessionExpired') } },
  });
}

function* handleSetUserRole(action) {
  if (!action.isRedirect) return;
  if (action.payload === 'admin' || action.payload === 'group_manager' || action.payload === 'chain_manager') {
    yield history.push('/organizations');
    return;
  } else if (action.payload === 'group_admin') {
    yield history.push('/admins');
    return;
  } else {
    yield history.push('/users');
    return;
  }

  //  if (action.isRedirect) yield history.push("/users");
}

function* handlePasswordReset() {
  yield history.push('/login');
}

function* handleGetTokenSuccess(action) {
  const token = action.payload;
  const memberId = token?.memberId;
  if (memberId) {
    const memberInfo = yield api.getMember(memberId);
    if (!memberInfo.error && memberInfo.response) {
      syncLanguageFromUser(memberInfo.response.language || token?.language);
      const orgId = memberInfo?.response?.organization?.id;

      if (orgId) {
        const orgInfo = yield api.getOrganization(orgId);
        if (orgInfo.response && !orgInfo.error) {
          yield put({
            type: authActions.SET_PARENT_ORG,
            payload: orgInfo?.response,
          });
          yield put({
            type: authActions.SET_CAN_CREATE_LICENSES,
            payload: orgInfo?.response?.canCreateLicenses || false,
          });
        }
      }
    } else {
      syncLanguageFromUser(token?.language);
    }
  } else {
    syncLanguageFromUser(token?.language);
  }
}

/******************************************************************************/
/******************************* WATCHERS *************************************/
/******************************************************************************/

export function* watchGetToken() {
  yield takeEvery(authActions.GET_TOKEN.REQUEST, getToken);
}

export function* watchLoginUser() {
  yield takeEvery(authActions.LOGIN.REQUEST, loginUser);
}

export function* watchLoginSuccess() {
  yield takeEvery(authActions.LOGIN.SUCCESS, saveToken);
}

export function* watchLogout() {
  yield takeEvery(authActions.LOGOUT.REQUEST, removeToken);
}

export function* watchGetTokenFailure() {
  yield takeEvery(authActions.GET_TOKEN.FAILURE, removeTokenNoRedirect);
}

export function* watchGetTokenSuccess() {
  yield takeEvery(authActions.GET_TOKEN.SUCCESS, handleGetTokenSuccess);
}

export function* watchRefreshToken() {
  yield takeEvery(authActions.GET_NEW_TOKEN.REQUEST, refreshToken);
}

export function* watchRefreshTokenSuccess() {
  yield takeEvery(authActions.GET_NEW_TOKEN.SUCCESS, saveToken);
}

export function* watchRefreshTokenFailure() {
  yield takeEvery(authActions.GET_NEW_TOKEN.FAILURE, handleInvalidToken);
}

export function* watchSetUserRole() {
  yield takeEvery('SET_USER_ROLE', handleSetUserRole);
}

export function* watchRequestPasswordResetRequest() {
  yield takeEvery(authActions.REQUEST_PASSWORD_RESET.REQUEST, requestPasswordReset);
}

export function* watchResetPasswordRequest() {
  yield takeEvery(authActions.RESET_PASSWORD.REQUEST, resetPassword);
}

export function* watchResetPasswordSuccess() {
  yield takeEvery(authActions.RESET_PASSWORD.SUCCESS, handlePasswordReset);
}

export function* watchGetRoleManagementRepresentationRequest() {
  yield takeEvery(authActions.GET_ROLE_MANAGEMENT_REPRESANTATION.REQUEST, getRoleManagementRepresentation);
}
